import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Avatar
} from '@mui/material';
import {
  People as PeopleIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { friendsApi } from '../api/apiService';
import { useAuth } from '../context/AuthContext';

interface InvitationDetails {
  invitation: {
    id: string;
    inviterKidId: string;
    inviteCode: string;
    status: string;
    expiresAt: string;
  };
  inviterKid: {
    id: string;
    name: string;
    totalPoints: number;
    coins: number;
    level: number;
  };
}

const AcceptInvitationPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, userType, kids } = useAuth();

  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitationDetails, setInvitationDetails] = useState<InvitationDetails | null>(null);
  const [selectedKidId, setSelectedKidId] = useState<string | null>(null);

  useEffect(() => {
    if (code) {
      sessionStorage.setItem('pendingFriendInviteCode', code);
      validateInvitation(code);
    }
  }, [code]);

  useEffect(() => {
    // Auto-select kid from URL param or first kid
    const kidIdParam = searchParams.get('kidId');
    if (kidIdParam) {
      setSelectedKidId(kidIdParam);
    } else if (kids && kids.length > 0) {
      setSelectedKidId(kids[0].id!);
    }
  }, [kids, searchParams]);

  const validateInvitation = async (inviteCode: string) => {
    try {
      setLoading(true);
      const response = await friendsApi.getInvitationByCode(inviteCode);
      setInvitationDetails(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid or expired invitation code');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!code || !selectedKidId) return;

    try {
      setAccepting(true);
      setError(null);
      await friendsApi.acceptInvitation(code, selectedKidId);
      sessionStorage.removeItem('pendingFriendInviteCode');

      // Navigate to friends page
      navigate(`/user/${selectedKidId}/friends`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleSelectKid = () => {
    navigate('/select-kid');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Not logged in
  if (!isAuthenticated) {
    return (
      <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
        <Card sx={{ borderRadius: 3, textAlign: 'center', py: 4 }}>
          <CardContent>
            <PeopleIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Friendship Invitation
            </Typography>
            {invitationDetails ? (
              <>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  <strong>{invitationDetails.inviterKid.name}</strong> wants to be your friend!
                  Please log in to accept this invitation.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleLogin}
                >
                  Log In to Accept
                </Button>
              </>
            ) : (
              <Alert severity="error">
                {error || 'Invalid invite code'}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Parent logged in but no kid selected
  if (userType === 'parent' && !selectedKidId) {
    return (
      <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
        <Card sx={{ borderRadius: 3, textAlign: 'center', py: 4 }}>
          <CardContent>
            <PeopleIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Select a Kid
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Please select which kid wants to be friends with <strong>{invitationDetails?.inviterKid.name}</strong>.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleSelectKid}
            >
              Select Kid
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {invitationDetails && (
        <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
          <Box
            sx={{
              background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
              py: 4,
              px: 3
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'rgba(255,255,255,0.2)',
                mx: 'auto',
                mb: 2
              }}
            >
              <PeopleIcon sx={{ fontSize: 40, color: 'white' }} />
            </Avatar>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
              Friend Invitation!
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              {invitationDetails.inviterKid.name} wants to be your friend
            </Typography>
          </Box>
          <CardContent sx={{ py: 4 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              By accepting, you'll send a friend request to <strong>{invitationDetails.inviterKid.name}</strong>.
              They'll need to confirm before you become friends.
            </Typography>

            {/* Inviter kid info */}
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {invitationDetails.inviterKid.name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {invitationDetails.inviterKid.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Level {invitationDetails.inviterKid.level} | {invitationDetails.inviterKid.totalPoints} points
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Kid selector for parents */}
            {userType === 'parent' && kids && kids.length > 1 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Accepting for:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {kids.map((kid) => (
                    <Button
                      key={kid.id}
                      variant={selectedKidId === kid.id ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setSelectedKidId(kid.id!)}
                    >
                      {kid.name}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={accepting ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                onClick={handleAcceptInvitation}
                disabled={accepting}
                sx={{
                  bgcolor: '#ec4899',
                  '&:hover': { bgcolor: '#db2777' }
                }}
              >
                {accepting ? 'Sending...' : 'Send Friend Request'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {!invitationDetails && !loading && (
        <Card sx={{ borderRadius: 3, textAlign: 'center', py: 4 }}>
          <CardContent>
            <PeopleIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Invalid Invitation
            </Typography>
            <Typography variant="body1" color="text.secondary">
              This invitation link is invalid or has expired.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AcceptInvitationPage;